import { ProvisionType } from "@redotech/congo-enums/provision-type";
import { UpsellBillingTrigger } from "@redotech/congo-enums/upsell-billing-trigger";
import { Currency } from "@redotech/money/currencies";
import { isBelowStripeMinimumCharge } from "@redotech/money/stripe-minimum-charge";
import { isDraftingExchangeReturn } from "@redotech/redo-return-core-common/draft-return/draft-return-predicates";
import { chargesExchangeShippingViaStripe } from "@redotech/redo-return-core-common/returns/return-totals/exchange-shipping-billing";
import { RepairFeeCollectionStrategy } from "@redotech/redo-return-warranties-common/warranties";
import type { DraftReturn, ReturnTotalCalculations } from "./draft-return";
import { getBaseRepairFee } from "./util";
/**
 * True when the outstanding balance is billed on the exchange invoice rather
 * than charged at submit — the only case Shopify store credit can cover.
 */
export function isBalanceInvoicedLater(
  upsellBillingTrigger: UpsellBillingTrigger | null | undefined,
): boolean {
  return (
    upsellBillingTrigger != null &&
    upsellBillingTrigger !== UpsellBillingTrigger.ReturnCreated
  );
}

export interface PaymentTimingSplit {
  /** Charged in the portal at submission (return shipping + any immediate balance). */
  amountDueAtSubmission: number;
  /** Billed later on the exchange invoice; not collected at submission. */
  amountInvoicedLater: number;
}

/** Splits the total owed into what is charged now vs. billed on the exchange
 * invoice later, matching the customer portal's due-now / due-later breakdown. */
export function splitPaymentTiming(
  draftReturn: DraftReturn,
  calculations: ReturnTotalCalculations,
): PaymentTimingSplit {
  const { totalPaymentOwed, paymentOwed } = calculations;
  const amountInvoicedLater = isExchangeBalanceInvoicedLater(draftReturn)
    ? roundToCents(Math.max(0, paymentOwed))
    : 0;
  return {
    amountInvoicedLater,
    amountDueAtSubmission: roundToCents(totalPaymentOwed - amountInvoicedLater),
  };
}

function isExchangeBalanceInvoicedLater(draftReturn: DraftReturn): boolean {
  return (
    isBalanceInvoicedLater(
      draftReturn.upsellBillingTrigger ??
        UpsellBillingTrigger.ExchangeOrderCreated,
    ) && draftReturn.provisionType !== ProvisionType.Instant
  );
}

function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function shouldCollectPayment(
  draftReturn: DraftReturn,
  calculations: ReturnTotalCalculations,
  options: { forceExchangeStripeBilling?: boolean } = {},
): boolean {
  const {
    totalShippingFee,
    exchangeShippingFee,
    totalRepairFee,
    skipPayment,
    labelDeducted,
    totalPaymentOwed,
  } = calculations;

  // Regular and exchange shipping fees follow separate billing strategies.
  const regularShippingFee = totalShippingFee - exchangeShippingFee;
  const needsToPayRegularShippingFee =
    regularShippingFee > 0 && !skipPayment && !labelDeducted;

  // Prefer the calculator decision, then derive it for legacy callers.
  const chargeExchangeShippingViaStripe =
    calculations.chargesExchangeShippingViaStripe ??
    chargesExchangeShippingViaStripe({
      upsellBillingTrigger: draftReturn.upsellBillingTrigger,
      forceExchangeStripeBilling: options.forceExchangeStripeBilling,
    });
  // Landed cost rides on the exchange shipping charge, so a $0 rate with
  // duties still needs a card.
  const needsToPayExchangeShippingFee =
    exchangeShippingFee + (calculations.exchangeLandedCost ?? 0) > 0 &&
    chargeExchangeShippingViaStripe;

  const hasInstantRepairFee =
    getBaseRepairFee(totalRepairFee) > 0 &&
    draftReturn.repairFeeCollectionStrategy ===
      RepairFeeCollectionStrategy.INSTANT;

  const hasExchangeReturn = isDraftingExchangeReturn(draftReturn);
  const isInstantProvision =
    draftReturn.provisionType === ProvisionType.Instant;

  const isReturnCreatedUpsellBilling =
    (draftReturn.upsellBillingTrigger ??
      UpsellBillingTrigger.ExchangeOrderCreated) ===
      UpsellBillingTrigger.ReturnCreated && totalPaymentOwed > 0;

  return (
    (isInstantProvision && hasExchangeReturn) ||
    needsToPayRegularShippingFee ||
    needsToPayExchangeShippingFee ||
    hasInstantRepairFee ||
    (isReturnCreatedUpsellBilling &&
      !(
        draftReturn.waiveOutstandingBalance && !draftReturn.keepFeesAndBonuses
      ) &&
      !isUpsellFeeWaived(draftReturn, calculations))
  );
}

export function isOutstandingBalanceWaived(params: {
  manuallyWaived: boolean;
  amount: number;
  currency: Currency;
}): boolean {
  const { manuallyWaived, amount, currency } = params;
  if (amount <= 0) {
    return false;
  }
  return manuallyWaived || isBelowStripeMinimumCharge(amount, currency);
}

export function isUpsellFeeWaived(
  draftReturn: DraftReturn,
  calculations: ReturnTotalCalculations,
): boolean {
  const currency = draftReturn.currency || Currency.USD;
  const { totalShippingFee, exchangeShippingFee, totalPaymentOwed } =
    calculations;

  const isReturnCreatedUpsellBilling =
    (draftReturn.upsellBillingTrigger ??
      UpsellBillingTrigger.ExchangeOrderCreated) ===
      UpsellBillingTrigger.ReturnCreated && totalPaymentOwed > 0;

  if (!isReturnCreatedUpsellBilling) {
    return false;
  }

  const shippingFee = totalShippingFee - exchangeShippingFee;
  // Rounded so float residue (14.8 - 12 - 2.8) isn't read as a sub-minimum fee.
  const upsellFeeAmount = roundToCents(
    Math.max(
      0,
      totalPaymentOwed -
        shippingFee -
        (exchangeShippingFee || 0) -
        (calculations.exchangeLandedCost ?? 0),
    ),
  );

  return (
    upsellFeeAmount > 0 && isBelowStripeMinimumCharge(upsellFeeAmount, currency)
  );
}
